import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppAdminFormLabel,
  AppBreadcrumb,
  AppButton,
  AppCard,
  AppContainer,
  AppTextField,
} from '@/components/atoms';
import { DeleteFill } from '@/components/atoms/AppIcon';
import { AuditInfo, StandardDeleteConfirmContent } from '@/components/molecules';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import useAppToast from '@/hooks/useAppToast';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import {
  getLatlonCache,
  updateLatlonCache,
  deleteLatlonCache,
} from '@/services/admin/latlon-caches';
import type { LatlonCacheUpdatePayload } from '@/services/admin/latlon-caches';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';
import { LATITUDE_REGEX, LONGITUDE_REGEX } from '@/utils/geoCoordinate';

const SiteAdminLatlonCachesEditPage: React.FC = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const currentSite = site ?? 'super-admin';
  const recordId = Number(id) || 0;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const openStandardDeleteConfirm = useStandardDeleteConfirm();

  const locationState = location.state as { listSearch?: string; fromDetail?: boolean } | null;
  const listSearch = locationState?.listSearch ?? '';
  const fromDetail = locationState?.fromDetail ?? false;
  const listPath = `/${currentSite}/admin/latlon_caches${listSearch}`;
  const listPathNoParams = `/${currentSite}/admin/latlon_caches`;
  const detailPath = `/${currentSite}/admin/latlon_caches/${recordId}`;

  const navigateAfterUpdate = () => {
    queryClient.removeQueries({ queryKey: ['admin', 'latlon-cache-detail'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'latlon-caches'] });
    navigate(listPathNoParams, { replace: true });
  };

  const navigateToList = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'latlon-caches'] });
    navigate(listPath, { replace: true });
  };

  const { data, status, isFetching } = useQuery({
    queryKey: ['admin', 'latlon-cache-detail', currentSite, recordId],
    queryFn: () => getLatlonCache(currentSite, recordId),
    enabled: !!recordId,
    staleTime: 0,
    gcTime: 0,
  });

  const record = data?.data;
  const isInitialLoading = status === 'pending' || (isFetching && !record);

  const [address, setAddress] = useState('');
  const [addressGeop, setAddressGeop] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (record) {
      setAddress(record.address ?? '');
      setAddressGeop(record.address_geop ?? '');
      setLatitude(String(record.latitude ?? ''));
      setLongitude(String(record.longitude ?? ''));
    }
  }, [record]);

  if (isInitialLoading) return <LoadingTemplate />;
  if (status === 'error' || (!isFetching && !record)) return <SiteAdminNotFoundTemplate />;

  const breadcrumbItems = [
    { title: '緯度経度キャッシュ', href: listPath },
    { title: `編集:${recordId}` },
  ];

  const validateForm = (): boolean => {
    const errors: Record<string, string[]> = {};

    if (!address.trim()) {
      errors.address = ['住所は必須です。'];
    } else if (address.length > 100) {
      errors.address = ['住所は100文字以下にしてください。'];
    }

    if (addressGeop.length > 100) {
      errors.address_geop = ['GeOAP住所は100文字以下にしてください。'];
    }

    if (!latitude.trim()) {
      errors.latitude = ['緯度は必須です。'];
    } else if (isNaN(Number(latitude))) {
      errors.latitude = ['緯度は数字にしてください。'];
    } else if (!LATITUDE_REGEX.test(latitude.trim())) {
      errors.latitude = ['緯度の書式が正しくありません。'];
    }

    if (!longitude.trim()) {
      errors.longitude = ['経度は必須です。'];
    } else if (isNaN(Number(longitude))) {
      errors.longitude = ['経度は数字にしてください。'];
    } else if (!LONGITUDE_REGEX.test(longitude.trim())) {
      errors.longitude = ['経度の書式が正しくありません。'];
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      toast.error('エラーが発生しました。');
      return;
    }
    setIsUpdating(true);
    try {
      const payload: LatlonCacheUpdatePayload = {
        address,
        address_geop: addressGeop || null,
        latitude,
        longitude,
      };
      await updateLatlonCache(currentSite, recordId, payload);
      toast.success('更新しました。');
      navigateAfterUpdate();
    } catch (err: unknown) {
      if (isNotFoundError(err)) {
        toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
        navigate(`/${currentSite}/admin/404`, { replace: true });
        return;
      }
      const axiosErr = err as {
        response?: { status?: number; data?: { errors?: Record<string, string[]> } };
      };
      if (axiosErr.response?.status === 422 && axiosErr.response.data?.errors) {
        setFieldErrors(axiosErr.response.data.errors);
        toast.error('エラーが発生しました。');
      } else {
        toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    openStandardDeleteConfirm({
      content: <StandardDeleteConfirmContent />,
      okButtonProps: { loading: isDeleting },
      onOk: async () => {
        setIsDeleting(true);
        try {
          await deleteLatlonCache(currentSite, recordId);
          toast.success('削除しました。');
          navigateToList();
        } catch (err: unknown) {
          if (isNotFoundError(err)) {
            toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
            navigate(`/${currentSite}/admin/404`, { replace: true });
            return;
          }
          toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const clearFieldError = (name: string) => {
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const renderField = (
    label: string,
    name: string,
    value: string,
    onChange: (v: string) => void,
    required = false
  ) => (
    <div className="mb-4">
      <AppAdminFormLabel label={label} required={required} />
      <AppTextField
        value={value}
        onChange={e => {
          onChange(e.target.value);
          clearFieldError(name);
        }}
        status={fieldErrors[name] ? 'error' : undefined}
        className="w-full"
      />
      {fieldErrors[name]?.map((msg, i) => (
        <p key={i} className="mt-1 text-sm text-red-600">
          {msg}
        </p>
      ))}
    </div>
  );

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} />

      <AppCard title={`緯度経度キャッシュ編集:${recordId}`}>
        <p className="text-sm">治療院ID：{record?.clinic_number ?? ''}</p>

        <div className="mt-5">
          {renderField('住所', 'address', address, setAddress, true)}
          {renderField('GeOAP住所', 'address_geop', addressGeop, setAddressGeop)}
          {renderField('緯度', 'latitude', latitude, setLatitude, true)}
          {renderField('経度', 'longitude', longitude, setLongitude, true)}

          <div className="flex gap-3 mt-6">
            <AppButton
              icon={<DeleteFill />}
              danger
              loading={isDeleting}
              disabled={isDeleting || isUpdating}
              onClick={handleDelete}
            >
              削除
            </AppButton>
            <AppButton
              type="primary"
              loading={isUpdating}
              disabled={isDeleting}
              onClick={handleUpdate}
            >
              更新
            </AppButton>
            <AppButton
              onClick={() => {
                if (fromDetail) {
                  queryClient.invalidateQueries({ queryKey: ['admin', 'latlon-cache-detail'] });
                  navigate(detailPath, { state: { listSearch } });
                } else {
                  queryClient.invalidateQueries({ queryKey: ['admin', 'latlon-caches'] });
                  navigate(listPath);
                }
              }}
              disabled={isUpdating || isDeleting}
            >
              前画面に戻る
            </AppButton>
          </div>

          <AuditInfo
            createdBy={record?.created_by}
            updatedBy={record?.updated_by}
            updatedAt={record?.updated_at}
          />
        </div>
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminLatlonCachesEditPage;
